/**
 * @module CreateBellBadge
 * @package @geenius/notifications-solidjs-css
 * @description Provides the compact bell badge state used by the bell trigger
 * in the SolidJS vanilla CSS notifications variant.
 */

import type { Accessor } from 'solid-js'
import { createMemo } from 'solid-js'

/**
 * Bell badge state derived from the unread count.
 */
export interface BellBadgeState {
  count: Accessor<number>
  hasUnread: Accessor<boolean>
  displayCount: Accessor<string>
}

/**
 * Derives bell badge state from an unread count accessor.
 *
 * @param unreadCount Current unread count accessor.
 * @returns Derived badge state for rendering.
 */
export function createBellBadge(
  unreadCount: Accessor<number | undefined>,
): BellBadgeState {
  const count = createMemo(() => unreadCount() ?? 0)
  const hasUnread = createMemo(() => count() > 0)
  const displayCount = createMemo(() => (count() > 99 ? '99+' : String(count())))

  return { count, hasUnread, displayCount }
}
